import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useParams } from "react-router-dom";

// Material UI components
import { Card } from "@mui/material";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import FormTextArea from "components/Form/FTextArea";

// Constants
import Constants, { defaultData } from "utils/Constants";

// Thunk
import { getQHSEDprData } from "redux/Thunks/Dpr";

// Slice
import {
  setQhseRemarks,
  updateIsLatestDataApiCompleted,
  updateQhseKpiValue,
  selectQhseStatus,
} from "redux/Slice/Dpr";
import { openSnackbar } from "redux/Slice/Notification";

// Data
import QhseDPRData from "../data/qhseData";

function QhseTab() {
  const dispatch = useDispatch();

  const { id } = useParams();

  const { dprData, displayedDprTabsObj, isDprDetailReqCompleted } = useSelector(
    (state) => state.dprs
  );
  const qhseLoading = useSelector(selectQhseStatus);

  // API calls starts here
  const getQHSEData = async () => {
    const response = await dispatch(getQHSEDprData(id));

    // QHSE thunk resolves with the HTTP status code, so only a 2xx counts as success
    const status = response?.payload?.status;
    const isSuccess = status >= 200 && status < 300;

    // Display Error message if API call fails
    if (!isSuccess) {
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
    return isSuccess;
  };

  const qhseKpiList = useMemo(() => {
    const legacy = dprData?.qhse?.qhseSummary || [];
    const kpis = dprData?.qhse?.kpis?.length ? [...dprData.qhse.kpis] : [];
    const sortedKpis = [...kpis].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    return [...legacy, ...sortedKpis];
  }, [dprData?.qhse?.kpis, dprData?.qhse?.qhseSummary]);

  const handleKpiChange = useCallback(
    (kpiId, field, value) => {
      dispatch(updateQhseKpiValue({ kpiId, field, value }));
    },
    [dispatch]
  );

  const { qhseColumns, qhseRows } = QhseDPRData(qhseKpiList, {
    editable: true,
    onChange: handleKpiChange,
  });

  useEffect(() => {
    if (isDprDetailReqCompleted) {
      getQHSEData();
    }
  }, [isDprDetailReqCompleted]);

  useEffect(() => {
    if (displayedDprTabsObj?.qhseTab > 0) {
      getQHSEData()
        .then((isSuccess) => dispatch(updateIsLatestDataApiCompleted(isSuccess)))
        .catch(() => dispatch(updateIsLatestDataApiCompleted(false)));
    }
  }, [displayedDprTabsObj?.qhseTab]);

  return (
    <MDBox width="100%">
      <Card id="progress-tab" sx={{ mt: 1, px: 2 }}>
        <MDBox p={2} width="100%" display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6" fontWeight="medium">
            QHSE
          </MDTypography>
        </MDBox>
        <MDBox>
          <Card>
            <MDBox>
              <DataTable
                table={{ columns: qhseColumns, rows: qhseRows }}
                isSorted={false}
                entriesPerPage={{ defaultValue: defaultData.MAX_PER_PAGE }}
                showTotalEntries={false}
                noEndBorder
                loading={qhseLoading}
                licenseRequired
              />
            </MDBox>
          </Card>
        </MDBox>
        <MDBox mb={2} mt={2}>
          <MDBox pl={2}>
            <MDTypography variant="h6" fontWeight="medium">
              Remarks
            </MDTypography>
          </MDBox>
          <FormTextArea
            value={dprData?.qhse?.remarks}
            name="remarks"
            placeholder="Add Description here..."
            backgroundColor="white"
            handleChange={(e) => {
              dispatch(setQhseRemarks(e.target.value));
            }}
          />
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default QhseTab;
